import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Clock, Users, History, ScrollText, FileText, Edit3, Trash2, Send, FileCheck, FileX, Unlock, MessageCircle, MessageSquare, RefreshCw } from 'lucide-react';
import api from '../../services/api';

const LOG_ACTION_CONFIG = {
    doc_created:          { icon: FileText,       color: 'text-blue-600',   bg: 'bg-blue-50 dark:bg-blue-900/20' },
    doc_updated:          { icon: Edit3,           color: 'text-amber-600',  bg: 'bg-amber-50 dark:bg-amber-900/20' },
    doc_deleted:          { icon: Trash2,          color: 'text-red-600',    bg: 'bg-red-50 dark:bg-red-900/20' },
    doc_published:        { icon: Send,            color: 'text-teal-600',   bg: 'bg-teal-50 dark:bg-teal-900/20' },
    doc_approved:         { icon: FileCheck,       color: 'text-green-600',  bg: 'bg-green-50 dark:bg-green-900/20' },
    doc_rejected:         { icon: FileX,           color: 'text-red-600',    bg: 'bg-red-50 dark:bg-red-900/20' },
    doc_unlocked:         { icon: Unlock,          color: 'text-purple-600', bg: 'bg-purple-50 dark:bg-purple-900/20' },
    doc_comment_added:    { icon: MessageCircle,   color: 'text-sky-600',    bg: 'bg-sky-50 dark:bg-sky-900/20' },
    doc_comment_deleted:  { icon: MessageSquare,   color: 'text-orange-600', bg: 'bg-orange-50 dark:bg-orange-900/20' },
    default:              { icon: History,         color: 'text-muted',      bg: 'bg-surface-2' },
};

export default function DocHistoryPage() {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    
    // Determine the base path to go back correctly (admin/docs or panel/docs)
    const basePath = location.pathname.startsWith('/admin') ? '/admin/docs' : '/panel/docs';

    const [logs, setLogs] = useState([]);
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [docTitle, setDocTitle] = useState('');

    useEffect(() => {
        // Fetch document info for the title and versions
        api.get(`/docs/${id}`)
            .then(r => { setDocTitle(r.data.title); })
            .catch(() => { });

        api.get(`/docs/${id}/versions`)
            .then(r => setVersions(r.data))
            .catch(() => { });

        // Fetch logs
        setLoading(true);
        api.get(`/docs/${id}/activities`)
            .then(r => setLogs(r.data))
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    }, [id]);

    const uniqueUsers = new Set(logs.map(l => l.user?.id).filter(Boolean)).size;
    const totalActions = logs.length;
    const lastDate = logs[0]?.created_at
        ? (() => {
            const iso = logs[0].created_at.endsWith('Z') || logs[0].created_at.includes('+') ? logs[0].created_at : logs[0].created_at + 'Z';
            const d = new Date(iso);
            return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()}`;
        })()
        : '—';

    return (
        <div className="space-y-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-4">
                <button
                    onClick={() => navigate(`${basePath}/${id}`)}
                    className="p-2 rounded-lg bg-surface hover:bg-surface-2 border border-border text-muted hover:text-primary transition-colors flex-shrink-0"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                    <h2 className="text-2xl font-bold text-primary line-clamp-1">
                        {docTitle || t('docs.tab_content', 'Hujjat')} — {t('docs.doc_history', 'Faoliyat tarixi')}
                    </h2>
                    <p className="text-sm text-muted">{t('docs.doc_history_desc', 'Hujjat bo\'yicha amallar va versiyalar tarixi')}</p>
                </div>
            </div>

            {/* Versions block */}
            {versions.length > 0 && (
                <div className="bg-surface border border-border rounded-2xl p-5 shadow-sm space-y-3">
                    <h3 className="text-base font-bold text-primary flex items-center gap-2">
                        <ScrollText className="w-5 h-5 text-brand" />
                        {t('docs.document_versions', 'Hujjat versiyalari')} ({versions.length})
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {versions.map(v => (
                            <div
                                key={v.id || v.version_number}
                                onClick={() => navigate(`${basePath}/${id}?version=${v.version_number}`)}
                                className="p-4 rounded-xl border border-border bg-surface-2/40 hover:bg-surface-2 hover:border-brand/40 transition-all cursor-pointer flex flex-col justify-between group"
                            >
                                <div>
                                    <div className="flex items-center justify-between gap-2 mb-2">
                                        <span className="px-2.5 py-1 rounded-md bg-brand/10 text-brand font-bold text-xs">
                                            v{v.version_number}
                                        </span>
                                        <span className="text-xs text-muted font-mono">
                                            {new Date(v.created_at).toLocaleDateString()}
                                        </span>
                                    </div>
                                    <p className="text-sm font-medium text-primary line-clamp-1 group-hover:text-brand transition-colors">
                                        {v.pdf_filename || v.title}
                                    </p>
                                    {v.change_summary && (
                                        <p className="text-xs text-muted mt-1 line-clamp-2">{v.change_summary}</p>
                                    )}
                                </div>
                                <div className="mt-3 pt-2 border-t border-border/50 flex items-center justify-between text-xs font-semibold text-brand">
                                    <span>{t('docs.view_version', 'Versiyani ko\'rish')} →</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Stats row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                    { icon: Users,   label: t('history.users', 'Foydalanuvchilar'), value: uniqueUsers,  color: 'text-brand',      bg: 'bg-brand/10' },
                    { icon: History, label: t('docs.all_statuses', 'Jami amallar'),     value: totalActions, color: 'text-success',    bg: 'bg-surface-2 border border-border' },
                    { icon: Clock,   label: t('docs.updated_at', 'Oxirgi amal'),      value: lastDate,     color: 'text-warning',    bg: 'bg-surface-2 border border-border' },
                ].map(({ icon: Icon, label, value, color, bg }) => (
                    <div key={label} className="bg-surface border border-border rounded-2xl p-5 flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${bg}`}>
                            <Icon className={`w-6 h-6 ${color}`} />
                        </div>
                        <div>
                            <p className="text-xs text-muted font-semibold uppercase tracking-wider">{label}</p>
                            <p className={`text-2xl font-bold ${color}`}>{value}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Table */}
            <div className="bg-surface border border-border rounded-2xl overflow-hidden shadow-sm">
                <table className="w-full text-sm text-left">
                    <thead className="bg-surface-2 border-b border-border text-muted text-xs uppercase">
                        <tr>
                            <th className="px-6 py-4 font-semibold tracking-wider">{t('docs.history_action', 'Amal')}</th>
                            <th className="px-6 py-4 font-semibold tracking-wider">{t('docs.history_user', 'Foydalanuvchi')}</th>
                            <th className="px-6 py-4 font-semibold tracking-wider">{t('docs.history_detail', 'Tafsilot')}</th>
                            <th className="px-6 py-4 font-semibold tracking-wider whitespace-nowrap">{t('docs.history_date', 'Sana / Vaqt')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {loading ? (
                            <tr><td colSpan={4} className="px-6 py-16 text-center text-muted"><RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" /> {t('docs.loading', 'Yuklanmoqda...')}</td></tr>
                        ) : error ? (
                            <tr><td colSpan={4} className="px-6 py-16 text-center text-error">{error}</td></tr>
                        ) : logs.length === 0 ? (
                            <tr>
                                <td colSpan={4} className="px-6 py-16 text-center text-muted">
                                    <History className="w-12 h-12 opacity-20 mx-auto mb-3" />
                                    {t('docs.no_history', 'Hali hech qanday faoliyat yo\'q')}
                                </td>
                            </tr>
                        ) : logs.map(log => {
                            const cfg = LOG_ACTION_CONFIG[log.action] || LOG_ACTION_CONFIG.default;
                            const Icon = cfg.icon;
                            const dateStr = log.created_at ? (() => {
                                const iso = log.created_at.endsWith('Z') || log.created_at.includes('+') ? log.created_at : log.created_at + 'Z';
                                const d = new Date(iso);
                                return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()}, ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
                            })() : '—';
                            
                            const translateLogDetail = (details) => {
                                if (!details) return '—';
                                
                                const prefixes = [
                                    "Hujjat yaratildi: ",
                                    "Hujjat tahrirlandi: ",
                                    "Izoh qo'shildi: ",
                                    "Izoh o'chirildi: ",
                                    "Hujjat o'chirildi: ",
                                    "Hujjat qulfi ochildi: ",
                                    "Ko'ruvchi tayinlandi: ",
                                    "Ko'ruvchi olib tashlandi: ",
                                    "Ko'ruvchi ruxsati berildi (Yangi foydalanuvchi): ",
                                    "Ko'ruvchi ruxsati berildi: ",
                                    "Ko'ruvchi ruxsati olib tashlandi: ",
                                    "Tasdiqlovchi tayinlandi: ",
                                    "Tasdiqlovchi olib tashlandi: ",
                                    "Rad etdi: ",
                                    "Tasdiqladi: "
                                ];
                                
                                for (const prefix of prefixes) {
                                    if (details.startsWith(prefix)) {
                                        return details.replace(prefix, '');
                                    }
                                }

                                if (details.startsWith('Hujjat nashr qilindi v')) {
                                    const match = details.match(/Hujjat nashr qilindi v(\d+): (.*)/);
                                    if (match) return `v${match[1]}: ${match[2]}`;
                                }
                                
                                return details;
                            };

                            return (
                                <tr key={log.id} className="hover:bg-surface-2/50 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold border ${cfg.bg} ${cfg.color} border-current/20`}>
                                            <Icon className="w-4 h-4 flex-shrink-0" />
                                            {t(`docs.action_${log.action}`, t('docs.action_default', 'Faoliyat'))}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 font-bold text-primary whitespace-nowrap">
                                        {log.user
                                            ? `${log.user.last_name_ru} ${log.user.first_name_ru?.charAt(0)}.`
                                            : <span className="text-muted font-normal">—</span>}
                                    </td>
                                    <td className="px-6 py-4 text-muted max-w-md">
                                        <span className="line-clamp-2 text-sm leading-relaxed">{translateLogDetail(log.details)}</span>
                                    </td>
                                    <td className="px-6 py-4 text-muted whitespace-nowrap text-sm font-mono">
                                        {dateStr}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}